import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardTitle } from '@/components/ui/card';
import { PriceDisplay } from './PriceDisplay';
import { CardImage } from './CardImage';
import { ResponsiveImage } from './ResponsiveImage';
import { sampleCardImageUrl } from '../lib/cdn';
import { toBulbapediaThumbnail } from '../lib/bulbapedia';

/**
 * PackTile — a single Pack card in the browse grid.
 *
 * Phase 2 pivot (2026-04-11): no inventory. A pack is always
 * purchasable, so the tile just shows the image, name, and price.
 */

export interface PackTileProps {
  pack: {
    pack_id: string;
    name: string;
    set_slug: string;
    price_cents: number;
    cards_per_pack: number;
    pack_art_urls?: string[] | null;
    pack_art_urls_thumb?: string[] | null;
    pack_art_urls_2x?: string[] | null;
  };
  /**
   * Mark this tile as an LCP candidate (above the fold). Sets
   * `loading="eager"` and `fetchPriority="high"` on the image.
   * Default: false (image stays `loading="lazy"`).
   */
  priority?: boolean;
}

export function PackTile(props: PackTileProps): JSX.Element {
  const { pack, priority = false } = props;

  const artIndex = useMemo(() => {
    if (pack.pack_art_urls && pack.pack_art_urls.length > 0) {
      return Math.floor(Math.random() * pack.pack_art_urls.length);
    }
    return 0;
  }, [pack.pack_art_urls]);

  const imageUrl = useMemo(() => {
    if (pack.pack_art_urls && pack.pack_art_urls.length > 0) {
      const full = pack.pack_art_urls[artIndex] ?? '';
      // Use 340px thumbnail (~400KB) instead of full-size (~1.7MB) to
      // avoid overwhelming the browser with 9+ concurrent large PNGs.
      return toBulbapediaThumbnail(full, 340);
    }
    return sampleCardImageUrl(pack.set_slug);
  }, [pack.pack_art_urls, pack.set_slug, artIndex]);

  const hasPackArt = !!(pack.pack_art_urls && pack.pack_art_urls.length > 0);

  return (
    <Card
      data-testid="pack-tile"
      data-pack-id={pack.pack_id}
      className="transition-shadow duration-300 hover:shadow-[0_0_15px_2px_var(--accent-primary),0_0_30px_4px_var(--accent-secondary)]"
    >
      <Link to={`/pack/${pack.pack_id}`} className="no-underline">
        <CardContent className="p-0">
          <div className="aspect-[240/334] overflow-hidden rounded-t-lg bg-muted">
            {hasPackArt ? (
              <ResponsiveImage
                urls={{
                  thumb: pack.pack_art_urls_thumb?.[artIndex],
                  default: imageUrl,
                  _2x: pack.pack_art_urls_2x?.[artIndex],
                }}
                sizes="(max-width: 640px) 50vw, 300px"
                alt={`${pack.name} booster pack art`}
                width={240}
                height={334}
                className="h-full w-full object-cover"
                loading={priority ? 'eager' : 'lazy'}
                {...(priority ? { fetchPriority: 'high' as const } : {})}
              />
            ) : (
              <CardImage
                src={imageUrl}
                alt={`Sample card from ${pack.name}`}
                width={240}
                height={334}
              />
            )}
          </div>
          <CardTitle className="p-3 pb-0 text-h3 text-text-0 sm:p-4 sm:pb-0">
            {pack.name}
          </CardTitle>
        </CardContent>
      </Link>
      <CardFooter className="flex justify-between items-center p-3 sm:p-4">
        <PriceDisplay price_cents={pack.price_cents} status="OK" />
        <span className="text-small text-text-2">per pack</span>
      </CardFooter>
    </Card>
  );
}
